/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  FormControl,
  IconButton,
  FormHelperText as MuiFormHelperText,
  InputAdornment,
  Typography,
  styled,
  Link as MuiLink,
  Checkbox as MuiCheckbox,
  TextField as BaseTextField,
} from '@mui/material';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useAlertCallback} from '@jobseeker/lotus/hooks';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  CheckIcon,
  Eye,
  HiddenEye,
  UnCheckIcon,
} from '@jobseeker/lotus/assets/icons';
import Dialog from '@jobseeker/lotus/components/DialogWithBottomSheet';
import {AppState} from '@jobseeker/common-core/types';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import Input from '@jobseeker/lotus/elements/Input';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {useRouter} from 'next/router';
import Loader from '@jobseeker/lotus/components/Loader';
import authService from '@jobseeker/common-core/services/auth.service';
import {openUrl} from '@jobseeker/common-core/utils/common';

const TextField = styled(BaseTextField)(({theme}) => ({
  'textarea::placeholder': {
    fontStyle: 'italic',
  },
  '.MuiInputBase-root': {
    fontSize: 10,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },
  fieldset: {
    borderColor: '#7E7E7E33',
  },
  '.Mui-focused': {
    '&.Mui-focused fieldset': {
      borderColor: '#91B879',
      border: '1px solid',
    },
  },
}));

// import Checkbox from '@jobseeker/lotus/elements/CheckBox';
const IconImage = styled(Image)(({theme}) => ({
  '.Mui-focusVisible &': {
    outline: '2px auto',
    outlineColor: theme.palette.primary.light,
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    // cursor: 'not-allowed !important',
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(57,75,89,.5)'
        : 'rgba(206,217,224,.5)',
  },
}));

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));
const Link = styled(MuiLink)(() => ({
  textDecoration: 'none',
  padding: 0,
  fontSize: 'inherit',
  cursor: 'pointer',
}));
const Checkbox = ({size, sx, ...props}: any) => {
  return (
    <MuiCheckbox
      sx={{
        '&:hover': {bgcolor: 'transparent'},
        width: 'fit-content',
        ...sx,
      }}
      disableRipple
      color="default"
      checkedIcon={
        <Image
          width={size ?? 20}
          height={size ?? 20}
          src={CheckIcon}
          alt="checkedIcon"
        />
      }
      icon={
        <IconImage
          width={size ?? 20}
          height={size ?? 20}
          src={UnCheckIcon}
          alt="UnCheckIcon"
        />
      }
      // inputProps={{}}
      {...props}
    />
  );
};

const CheckBoxWithLabel = ({title, subtitle, ...props}: any) => {
  return (
    <Box pt={1.5} display={'flex'} alignItems={'start'} gap={1}>
      <Checkbox
        size={16}
        sx={{
          '&.MuiCheckbox-root': {
            padding: 0,
            // marginTop: '-12px',
            display: 'inline-block',
          },
          // '.MuiFormControlLabel-label': {lineHeight: '0.8'},
          // '&.MuiFormControlLabel-root': {alignItems: 'flex-start'},
          // alignItems: 'flex-start',
        }}
        {...props}
      />
      <Box lineHeight={'18px'}>
        <Typography component={'p'} variant="subtitle2" color={'text.primary'}>
          {title}
        </Typography>
        <Typography
          component={'span'}
          variant="subtitle3"
          color={'text.secondary'}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const validationSchemaDeactivate = yup.object({
  foundJobViaIimjobs: yup.boolean(),
  foundJobViaOther: yup.boolean(),
  notGettingRelevantJobs: yup.boolean(),
  noValueForMemebrship: yup.boolean(),
  tooManyEmails: yup.boolean(),
  privacyConcerns: yup.boolean(),
  others: yup.boolean(),
  password: yup
    .string()
    .min(8, 'Password must be 8 characters or more')
    .required('Please enter your password'),
  textMsg: yup
    .string()
    // .test('textMsg', 'feedback is required', function (textMsg: any) {
    //   const {others} = this.parent;
    //   if (others && !textMsg) return false;
    //   else return true;
    // })
    .test(
      'textMsg',
      'Please enter a valid value. Spaces alone are not allowed.',
      function (textMsg: any) {
        if (textMsg === undefined) {
          return true;
        }
        return !!textMsg?.trim();
      }
    ),
});

const DeactivateForm = ({
  onSuccess,
  onError,
  onChange,
  onCancel,
  ...props
}: any) => {
  const [isFeedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      foundJobViaIimjobs: false,
      foundJobViaOther: false,
      notGettingRelevantJobs: false,
      noValueForMemebrship: false,
      tooManyEmails: false,
      privacyConcerns: false,
      others: false,
      textMsg: '',
      password: '',
    },
    validationSchema: validationSchemaDeactivate,
    onSubmit: async (values: any) => {
      const {
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      } = values;
      const bitsArray = [
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      ]
        .map(k => (k ? 1 : 0))
        .join('');

      const resp = await userService.deactivateAccount({
        password: values.password,
        reason: bitsArray,
        textMsg: values.textMsg,
      });

      if (resp?.error) {
        if (resp?.error?.message?.toLowerCase()?.includes('password')) {
          formik.setFieldError('password', resp?.error?.message);
        } else enqueueSnackbar(resp.error.message, {variant: 'error'});
        onError();
      } else {
        onSuccess();
      }
    },
    validate: values => {
      const {
        foundJobViaIimjobs,
        foundJobViaOther,
        notGettingRelevantJobs,
        noValueForMemebrship,
        tooManyEmails,
        privacyConcerns,
        others,
      } = values;
      const passed = !(
        foundJobViaIimjobs ||
        foundJobViaOther ||
        notGettingRelevantJobs ||
        noValueForMemebrship ||
        tooManyEmails ||
        privacyConcerns ||
        others
      );
      return passed
        ? {anyOneRequired: 'Please select any one of the options'}
        : undefined;
    },
    // validateOnChange: true,
    validateOnBlur: true,
    // validateOnMount: true,
  });
  const submitFeedback = async () => {
    const validations = await formik.validateForm(formik.values);
    if (validations.anyOneRequired || validations.textMsg) {
      return;
    }
    onChange({reason: '0000000'});
    setFeedbackSubmitted(true);
    formik.setErrors({password: ''});
  };
  const onForgotPassword = () => {
    props.onForgotPassword();
  };

  return (
    <>
      <Box
        sx={{
          display: isFeedbackSubmitted ? 'none' : 'block',
        }}
      >
        <Box>
          <Box maxWidth={600}>
            <CheckBoxWithLabel
              title="Found a job via iimjobs.com"
              subtitle="That’s great news! We’re happy to be of your service."
              name={'foundJobViaIimjobs'}
              onChange={formik.handleChange}
            />
            <CheckBoxWithLabel
              title="Found a job via some other Portal"
              subtitle={
                'Congratulations! We’re happy to be a part of your job-hunting journey.'
              }
              onChange={formik.handleChange}
              name={'foundJobViaOther'}
            />
            <CheckBoxWithLabel
              title="Not getting relevant jobs"
              subtitle={
                <>
                  You can always{' '}
                  <Link href="/personalize-jobfeed" target="_blank">
                    Personalize your jobfeed
                  </Link>{' '}
                  by selecting keywords related to your profile.
                </>
              }
              onChange={formik.handleChange}
              name={'notGettingRelevantJobs'}
            />
            <CheckBoxWithLabel
              title="Not getting enough value from Membership"
              subtitle={
                <>
                  You can{' '}
                  <Link href="/refund" target="_blank">
                    initiate a refund
                  </Link>{' '}
                  or for more information,{' '}
                  <Link href="/faq" target="_blank">
                    read
                  </Link>{' '}
                  what being a pro-member is like.
                </>
              }
              onChange={formik.handleChange}
              name={'noValueForMemebrship'}
            />
            <CheckBoxWithLabel
              title="Receiving too many E-mails"
              subtitle={
                <>
                  You can change your{' '}
                  <Link href="/settings" target="_blank">
                    mail notification settings
                  </Link>{' '}
                  and unsubscribe from mails you do not wish to receive.
                </>
              }
              onChange={formik.handleChange}
              name={'tooManyEmails'}
            />
            <CheckBoxWithLabel
              title="Privacy Concerns"
              subtitle={
                <>
                  You can{' '}
                  <Link
                    href="/settings?section=privacy-settings"
                    target="_blank"
                  >
                    block
                  </Link>{' '}
                  any company/recruiter from viewing your profile that you wish
                  to. Also, you can{' '}
                  <Link
                    href="/settings?section=privacy-settings"
                    target="_blank"
                  >
                    hide your profile
                  </Link>{' '}
                  from all recruiter/companies except who you explicitly apply
                  against.
                </>
              }
              onChange={formik.handleChange}
              name={'privacyConcerns'}
            />
            <CheckBoxWithLabel
              title="Others"
              subtitle=""
              onChange={formik.handleChange}
              name={'others'}
            />
          </Box>
          {formik.values.others && (
            <TextField
              name={'textMsg'}
              value={formik.values.textMsg}
              onChange={formik.handleChange}
              multiline
              maxRows={4}
              inputProps={{}}
              rows={4}
              sx={{width: '100%'}}
              placeholder="Your feedback is important to us, Please tell us how we can improve."
              variant="outlined"
            />
          )}
        </Box>
        {formik.touched && (
          <Box display={'flex'}>
            <Typography position={'absolute'} variant="caption" color="error">
              {formik.errors.anyOneRequired || formik.errors.textMsg}
            </Typography>
          </Box>
        )}
      </Box>
      <FormControl
        sx={{
          width: {xxs: '100%', md: '60%'},
          mt: 0.5,
          display: !isFeedbackSubmitted ? 'none' : 'block',
        }}
        error={Boolean(formik.errors.password)}
      >
        {/* <Typography variant="subtitle3">Enter the password</Typography> */}
        <Input
          variant={VersionVariants.VERSION_THREE}
          type={showNewPassword ? 'text' : 'Password'}
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.password)}
          placeholder="Enter the password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  // onMouseDown={handleMouseDown}
                  // edge="end"
                >
                  {showNewPassword ? (
                    <Image
                      src={Eye}
                      width={15}
                      height={15}
                      alt="show password"
                    />
                  ) : (
                    <Image
                      src={HiddenEye}
                      width={15}
                      height={15}
                      alt="hide password"
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            autoComplete: 'new-password',
          }}
        />

        <Box display={'flex'} justifyContent={'space-between'}>
          <FormHelperText
            sx={{
              opacity: Boolean(formik.errors.password) ? 1 : 0,
              width: {xxs: 'calc(100% - 150px)', md: '100%'},
            }}
          >
            <>{formik?.errors?.password || ''}</>
          </FormHelperText>
          <Typography
            variant="body3"
            pr={2}
            sx={{minWidth: '100%', textAlign: 'right'}}
          >
            <Link sx={{textDecoration: 'none'}} onClick={onForgotPassword}>
              Forgot Password?
            </Link>
          </Typography>
        </Box>
      </FormControl>
      <Box display={'flex'} gap={1.5} mt={4} mb={2}>
        <Button
          color="secondary"
          variant="contained"
          onClick={isFeedbackSubmitted ? formik.handleSubmit : submitFeedback}
          disabled={
            !isFeedbackSubmitted
              ? Boolean(formik.errors.textMsg) ||
                Boolean(formik.errors.anyOneRequired)
              : Boolean(formik.errors.password)
          }
        >
          {!isFeedbackSubmitted ? 'Continue' : 'Deactivate'}
        </Button>
        <Button onClick={onCancel} color="primary" variant="outlined">
          Cancel
        </Button>
      </Box>
      {}
    </>
  );
};

export const DeavtivateComponent: React.FC<object> = ({}) => {
  const initialHeaders = {
    title:
      'We’re sorry to see you go, please tell us your reason for deactivating.',
    subtitle: '',
  };
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [alertHeaders, setAlertHeaders] = useState(initialHeaders);
  const toggleDeactivateDialog = () => {
    setDeactivateDialogOpen(s => !s);
    setTimeout(() => {
      setAlertHeaders(initialHeaders);
    }, 50);
  };

  const onChange = (values: any) => {
    if (values.reason) {
      // setFormValues(values);
      setAlertHeaders({
        title: `By deactivating your account, all your applications will be withdrawn, your profile 
will no longer be visible to recruiters, and you will stop receiving emails from us`,
        subtitle: `Please provide your password if you still wish to deactivate your profile`,
      });
    }
  };

  const profile = useSelector((state: AppState) => state.user.profile);

  const [ForgotPasswordDialog, toggleForgotPassword] = useAlertCallback({
    title: (
      <>
        We’ve sent an email to {profile.email || 'your email'}
        <br /> Click the link in the email to rest your password.
      </>
    ),
    subtitle:
      'If you don’t see the email, check other places it might be, like your junk, spam, social, or other folders.',
    primaryAction: 'Okay',
    // secondaryAction: 'Cancel',
    primaryCallback: console.log,
    useBottomSheet: true,
  });

  const onSuccess = async () => {
    toggleLoaderDialog();
    // localStorage.setItem('accountDeactivated', 'yes');
    // cookieStore.set('accountDeactivated', 'yes');
    toggleDeactivateDialog();
    // window.location = '/deactivate_success';
    openUrl('/deactivate_success', '_self');
  };

  // useEffect(() => {
  //   toggleLoaderDialog();
  // }, []);

  const [LoaderDialog, toggleLoaderDialog] = useAlertCallback({
    alert: true,
    hideActions: true,
    primaryCallback: '',
    BodyContent: () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Loader />
        <Typography>Deactivating Account</Typography>
      </Box>
    ),
  });

  const onForgotPassword = async () => {
    const resInfo = await authService.forgotPassword('forgot', {
      email: profile.email,
    });
    toggleDeactivateDialog();
    toggleForgotPassword();
  };
  return (
    <>
      <Dialog
        open={deactivateDialogOpen}
        onClose={toggleDeactivateDialog}
        title={alertHeaders.title}
        subTitle={alertHeaders.subtitle}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: {xxs: 'calc(100vw - 16px)', md: '500px'},
            maxWidth: {xxs: 'calc(100vw - 16px)', md: '700px'},
            width: {xxs: 'calc(100vw - 16px)', md: '700px'},
          },
        }}
      >
        <DeactivateForm
          onSuccess={onSuccess}
          onChange={onChange}
          onError={console.log}
          onForgotPassword={onForgotPassword}
          onCancel={toggleDeactivateDialog}
        />
      </Dialog>
      <Box
        color="gray.dark"
        onClick={() => {}}
        data-testid="notification_list_item"
        sx={{
          position: 'relative',
          transition: 'all ease 0.2s',
          borderBottom: 'solid 1px',
          borderColor: 'border.lightGray',
        }}
      >
        <Box
          sx={{
            py: {xxs: 1.5, md: 2},
            px: {xxs: 1.5, md: 2},
          }}
        >
          <Box
            display={'flex'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Box display={'flex'} flexDirection={'column'}>
              <Typography
                fontWeight={500}
                color={'text.primary'}
                fontSize={'14px'}
                sx={{
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                Deactivate Account
              </Typography>
              <Typography
                fontWeight={400}
                fontSize={{xxs: 12, md: 14}}
                sx={{
                  color: 'var(--Grey-font-Color, #7E7E7E)',
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                Deactivating your account will withdraw your applications, hide
                your profile from recruiters, and stop all emails. You can
                reactivate your account at any time.
              </Typography>
              <Button
                variant="text"
                onClick={toggleDeactivateDialog}
                sx={{
                  padding: '0px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--brand-color, #49966E)',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Deactivate my account
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <ForgotPasswordDialog />
      <LoaderDialog />
    </>
  );
};
export const DeleteComponent: React.FC<object> = ({}) => {
  return (
    <Box
      color="gray.dark"
      onClick={() => {}}
      data-testid="notification_list_item"
      sx={{
        position: 'relative',
        transition: 'all ease 0.2s',
      }}
    >
      <Box
        sx={{
          py: {xxs: 1.5, md: 2},
          px: {xxs: 1.5, md: 2},
        }}
      >
        <Box
          display={'flex'}
          width={'100%'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              fontWeight={500}
              color={'text.primary'}
              fontSize={'14px'}
              sx={{
                lineClamp: '3',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'block',
                alignItems: 'center',
              }}
            >
              Delete Account
            </Typography>
            <Typography
              fontWeight={400}
              fontSize={{xxs: 12, md: 14}}
              sx={{
                color: 'var(--Grey-font-Color, #7E7E7E)',
                lineClamp: '3',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: 'block',
                alignItems: 'center',
              }}
            >
              Deleting your account will withdraw your applications, permanently
              remove your profile from our system, and stop all emails. This
              action is irreversible, and you will not be able to access your
              account again. If you wish to delete your account, please write to
              us at
              <Box component={'strong'}>
                &nbsp;
                <Link
                  color={'text.primary'}
                  fontWeight={500}
                  href={'mailto:info@iimjobs.com'}
                >
                  info@iimjobs.com
                </Link>
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
